import { ExecutionStep, StepType, TreeNode, TreeEdge } from '../types/rust-engine-interfaces';
import { step, arrayPayload, treePayload } from './fallback-helpers';

export function greedyFallback(id: string, raw: any): ExecutionStep[] {
  if (id.includes('activity')) return activitySelection(raw);
  if (id.includes('fractional')) return fractionalKnapsack(raw);
  if (id.includes('huffman')) return huffman(raw);
  return activitySelection(raw);
}

function activitySelection(raw: any): ExecutionStep[] {
  const activities = raw?.activities || [{ start: 1, end: 3 }, { start: 2, end: 5 }, { start: 4, end: 7 }, { start: 1, end: 8 }, { start: 5, end: 9 }];
  const sorted = [...activities].sort((a: any, b: any) => a.end - b.end); const selected: any[] = []; let lastEnd = 0;
  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload(sorted.map((a: any) => a.end), { highlightIndices: [] }), { line: 1, explanation: 'Activity Selection: sort by finish time.' }));
  for (const act of sorted) { s.push(step(s.length, StepType.Comparison, arrayPayload(sorted.map((a: any) => a.end), { currentIndex: sorted.indexOf(act), highlightIndices: [sorted.indexOf(act)] }), { line: 3, explanation: `Consider activity (${act.start},${act.end}).` })); if (act.start >= lastEnd) { selected.push(act); lastEnd = act.end; s.push(step(s.length, StepType.Update, arrayPayload(selected.map((a: any) => a.end), { currentIndex: selected.length - 1, highlightIndices: [selected.length - 1] }), { line: 4, explanation: `Select activity ending at ${act.end}.` })); } else { s.push(step(s.length, StepType.Decision, arrayPayload(sorted.map((a: any) => a.end), { currentIndex: sorted.indexOf(act), highlightIndices: [sorted.indexOf(act)] }), { line: 5, explanation: `Activity (${act.start},${act.end}) conflicts, skip.` })); } }
  s.push(step(s.length, StepType.Completion, arrayPayload(selected.map((a: any) => a.end), {}), { line: 7, explanation: `Selected ${selected.length} activities.` }));
  return s;
}

function fractionalKnapsack(raw: any): ExecutionStep[] {
  const weights: number[] = (raw?.weights || [10, 20, 30]).slice(0, 6); const values: number[] = (raw?.values || [60, 100, 120]).slice(0, 6); const capacity = raw?.capacity ?? 50;
  const items = weights.map((w, i) => ({ w, v: values[i]!, ratio: values[i]! / w })).sort((a, b) => b.ratio - a.ratio);
  let remaining = capacity; let total = 0; const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, arrayPayload(items.map((it) => it.ratio), { highlightIndices: [] }), { line: 1, explanation: `Fractional Knapsack: capacity ${capacity}, sort by value/weight ratio.` }));
  for (const it of items) { s.push(step(s.length, StepType.Comparison, arrayPayload(items.map((x) => x.ratio), { currentIndex: items.indexOf(it), highlightIndices: [items.indexOf(it)] }), { line: 3, explanation: `Item ratio ${it.ratio.toFixed(2)}.` })); if (remaining >= it.w) { remaining -= it.w; total += it.v; s.push(step(s.length, StepType.Update, arrayPayload([remaining, total], { currentIndex: 1, highlightIndices: [1] }), { line: 4, explanation: `Take full item, total value ${total}.` })); } else { total += it.ratio * remaining; remaining = 0; s.push(step(s.length, StepType.Update, arrayPayload([remaining, total], { currentIndex: 1, highlightIndices: [1] }), { line: 5, explanation: `Take fraction, total value ${total.toFixed(2)}.` })); } }
  s.push(step(s.length, StepType.Completion, arrayPayload([total], { sortedIndices: [0] }), { line: 7, explanation: `Maximum value: ${total.toFixed(2)}.` }));
  return s;
}

function huffman(raw: any): ExecutionStep[] {
  const chars: string[] = raw?.chars || ['a', 'b', 'c', 'd'];
  const freqs: number[] = (raw?.freqs || [5, 9, 12, 13]).slice(0, chars.length);

  interface HuffNode {
    id: string;
    value: string | number;
    freq: number;
    left?: string;
    right?: string;
  }

  let nextId = 0;
  const allNodes = new Map<string, HuffNode>();
  const roots: HuffNode[] = chars.map((c, i) => {
    const node: HuffNode = { id: `leaf-${c}-${i}`, value: `${c}:${freqs[i] ?? 0}`, freq: freqs[i] ?? 0 };
    allNodes.set(node.id, node);
    return node;
  });

  function serializeForest(): { nodes: TreeNode[]; edges: TreeEdge[] } {
    const nodes: TreeNode[] = [];
    const edges: TreeEdge[] = [];
    const visited = new Set<string>();

    function visit(node: HuffNode) {
      if (visited.has(node.id)) return;
      visited.add(node.id);
      const treeNode: TreeNode = { id: node.id, value: node.value };
      nodes.push(treeNode);
      if (node.left) {
        treeNode.left = node.left;
        edges.push({ from: node.id, to: node.left, type: 'left' });
        const child = allNodes.get(node.left);
        if (child) visit(child);
      }
      if (node.right) {
        treeNode.right = node.right;
        edges.push({ from: node.id, to: node.right, type: 'right' });
        const child = allNodes.get(node.right);
        if (child) visit(child);
      }
    }

    roots.forEach((root) => visit(root));
    nodes.forEach((n) => {
      const edge = edges.find((e) => e.to === n.id);
      if (edge) n.parent = edge.from;
    });
    return { nodes, edges };
  }

  const s: ExecutionStep[] = [];
  s.push(step(s.length, StepType.Initialization, treePayload(serializeForest().nodes, serializeForest().edges, { visitedNodes: roots.map((r) => r.id) }), { line: 1, explanation: 'Huffman Coding: initialize frequencies.' }));

  while (roots.length > 1) {
    roots.sort((a, b) => a.freq - b.freq);
    const left = roots.shift()!;
    const right = roots.shift()!;
    const merged: HuffNode = {
      id: `huff-${nextId++}`,
      value: left.freq + right.freq,
      freq: left.freq + right.freq,
      left: left.id,
      right: right.id,
    };
    allNodes.set(merged.id, merged);
    roots.push(merged);

    s.push(step(s.length, StepType.Update, treePayload(serializeForest().nodes, serializeForest().edges, { currentNode: merged.id, visitedNodes: roots.map((r) => r.id) }), { line: 3, explanation: `Merge ${left.value} (${left.freq}) and ${right.value} (${right.freq}) into ${merged.value}.` }));
  }

  const finalRoot = roots[0];
  s.push(step(s.length, StepType.Completion, treePayload(serializeForest().nodes, serializeForest().edges, { currentNode: finalRoot?.id, visitedNodes: finalRoot ? [finalRoot.id] : [] }), { line: 5, explanation: 'Huffman tree built.' }));
  return s;
}
